import { AiOutlineDelete, AiOutlineEdit, AiOutlineEye } from 'react-icons/ai';
import { TbRestore } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import { Card } from '../../../../../../components/card/card';
import { Chip } from '../../../../../../components/chip/chip';
import { IconButton } from '../../../../../../components/icon-button/icon-button';
import { HasAccess } from '../../../../../../features/has-access/has-access';
import { Table } from '../../../../../../features/table/table';
import { TableFilterDateTimeRange } from '../../../../../../features/table/table-filter/table-filter-date-time-range/table-filter-date-time-range';
import { TableFilterInput } from '../../../../../../features/table/table-filter/table-filter-input/table-filter-input';
import { TableFilterMultiSelect } from '../../../../../../features/table/table-filter/table-filter-multi-select/table-filter-multi-select';
import { formatRuDateString } from '../../../../../../helpers/date-time';
import { buildParams, concatPath } from '../../../../../../helpers/path';
import { serviceMap } from '../../../../../../helpers/services';
import { useLang } from '../../../../../../hooks/use-lang';
import { useModalParams } from '../../../../../../hooks/use-modal-params';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { ManagerPath, QrCodesPath, RootPath } from '../../../../../../routes/manager/manager-enums';
import { getBaseUrl } from '../../../../../../store/api/utils.ts/stand';
import { useBranchesStore } from '../../../../../../store/use-branch-store';
import { useQrStore } from '../../../../../../store/use-qr-store';
import { useTagStaffStore } from '../../../../../../store/use-tag-staff-store';
import s from './qr-table.module.css';

export const QrTable = () => {
  const { pagination, isLoadingFetch, staticList } = useQrStore();
  const { t, getLocale } = useLang();
  const { tags: tag } = useTagStaffStore();
  const { place } = useBranchesStore();
  const { addQueryParams } = useQueryParams();

  const { handleOpenModal: handleOpenQrModal } = useModalParams('viewStaticQr');

  return (
    <Card>
      <Table
        id="static-list"
        columnSettings
        withArchive
        frontPagination
        isLoading={isLoadingFetch}
        pagination={pagination}
        className={s.feature}
        onEmpty={{
          title: t('Список QR кодов пуст'),
          text: t('Создайте первый QR код с помощью кнопки выше'),
        }}
        cols={{
          name: {
            label: t('Название'),
            filter: (
              <TableFilterInput
                name="name"
                suggestOptions={staticList?.map((item) => getLocale(item.name))}
              />
            ),
          },
          description: {
            label: t('Описание'),
            filter: (
              <TableFilterInput
                name="description"
                suggestOptions={staticList?.map((item) => getLocale(item.description))}
              />
            ),
          },
          id: {
            label: 'ID',
            maxWidth: 250,
          },
          tags: {
            label: t('Ответственные группы'),
            filter: <TableFilterMultiSelect name="tags" options={tag} />,
          },
          services: {
            label: t('Сервисы'),
            filter: (
              <TableFilterMultiSelect
                name="services"
                options={Object.entries(serviceMap).map(([id, value]) => ({
                  id,
                  value,
                }))}
              />
            ),
          },
          place: {
            label: t('Филиал'),
            filter: (
              <TableFilterMultiSelect
                name="branch"
                options={(place || [])?.map((item) => ({
                  id: item.id.toString(),
                  value: getLocale(item.name),
                }))}
              />
            ),
          },
          createdAt: {
            label: t('Создан'),
            filter: <TableFilterDateTimeRange name="created_at" />,
          },
          actions: { label: t('Действия') },
        }}
        rows={staticList?.map((item) => {
          const branch = place?.find((place) => place.id == item.place_id);

          return {
            name: <div style={{ opacity: item.is_deleted ? 0.5 : 1 }}>{getLocale(item.name)}</div>,
            description: getLocale(item.description),
            id: item.is_deleted ? (
              t('Архивирован')
            ) : (
              <Link to={getBaseUrl() + '/qr/' + item.id} target="__blank">
                {item.id}
              </Link>
            ),
            tags: (
              <ul className={s.chips}>
                {item.tags?.map((tag) => (
                  <Chip variant="info" size="md" key={tag.id}>
                    {tag.value}
                  </Chip>
                ))}
              </ul>
            ),
            services: item.services.length ? (
              <ul className={s.chips}>
                {item.services?.map((service) => (
                  <Chip variant="warning" size="md" key={service}>
                    {t(serviceMap[service])}
                  </Chip>
                ))}
              </ul>
            ) : (
              <Chip variant="danger" size="md">
                {t('Не указано')}
              </Chip>
            ),
            place: branch?.is_deleted ? `*${getLocale(branch.name)}` : getLocale(branch?.name),
            createdAt: formatRuDateString(item.created_at),
            actions: item.is_deleted ? (
              <IconButton
                tooltip={t('Восстановить')}
                variant="secondary"
                size="sm"
                onClick={() =>
                  addQueryParams({
                    restoreId: String(item.id),
                    restoreName: getLocale(item.name),
                  })
                }
              >
                <TbRestore />
              </IconButton>
            ) : (
              <div className={s.actions}>
                <IconButton
                  tooltip={t('Просмотр')}
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    handleOpenQrModal({
                      qrId: item.id,
                      qrName: getLocale(item.name),
                    })
                  }
                >
                  <AiOutlineEye />
                </IconButton>

                <HasAccess service="qr" permissions={['write']}>
                  <Link
                    to={
                      concatPath([RootPath.Manager, ManagerPath.QrCodes, QrCodesPath.EditStatic]) +
                      buildParams({ id: item.id })
                    }
                  >
                    <IconButton tooltip={t('Редактировать')} size="sm" variant="secondary">
                      <AiOutlineEdit />
                    </IconButton>
                  </Link>
                  {!item.is_deleted && (
                    <IconButton
                      tooltip={t('Удалить')}
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        addQueryParams({
                          deleteId: item.id,
                          deleteName: getLocale(item.name),
                        })
                      }
                    >
                      <AiOutlineDelete />
                    </IconButton>
                  )}
                </HasAccess>
              </div>
            ),
          };
        })}
      />
    </Card>
  );
};
